import Globe from "globe.gl";
// import * as THREE from "three";
import { Color, TextureLoader } from "three";
import { createEl, checkParent } from "./utils";
import { pinIcon, ringIcon, discIcon, maps } from "./assets";

const defaultOptions = {
    flyToAltitude: 4,
    flyToAltitudeMobile: 2,
    countriesGeoJsonUrl: "./countries.geojson",
    autoAnimate: false,
    autoAnimateDuration: 8000,
    pointOfViewCoordinates: null,
    pinIconSizeSelected: "s",
    idleDuration: 7000,
    pinIconSelected: 1,
    pinData: [],
    pinIcons: [
        {
            id: 1,
            icon: pinIcon,
        },
        {
            id: 2,
            icon: ringIcon,
        },
        {
            id: 3,
            icon: discIcon,
        },
    ],
    pins: [],
    pinPulse: true,
    activeCountries: [],
    highlightedCountries: [],
    countryHighlightColor: "rgb(0, 255, 174)",
    countryHighlightDepth: 0,
    atmosphereColor: "rgb(78, 255, 255)",
    atmosphereAltitude: 0.001,
    ambientLightColor: 0x74c7ff,
    ambientLightIntensity: 0.3,
    directionalLightColor: 0xffffff,
    directionalLightIntensity: 1,
    map: "night1",
    surfaceType: "hex", // or image
    hexPolygonResolution: 1,
    hexPolygonMargin: 0.6,
    hexPolygonAltitude: 0.001,
    hexPolygonColor: "#ffffff",
    pinColor: "red",
    pinActiveColor: "purple",
    pinPopupCloseBtnEnabled: true,

    globeMaterialPolygons: {
        color: 0xffffff,
        shininess: 40,
        transparent: true,
        opacity: 0.3,
    },
    globeMaterialImage: {
        color: 0x000000,
        shininess: 20,
        transparent: false,
        opacity: 1,
    },
    trackballControls: {
        minDistance: 101,
        rotateSpeed: 5,
        zoomSpeed: 0.8,
    },
    controls: {
        enabledZoom: true,
        enabledPan: true,
        enableRotate: true,
    },

    flyToPinOnClick: true,
    createPopupHTML: null,
};

export class WpGlobe {
    constructor(container, options) {
        if (!container) {
            console.error("ThreeGlobeContoller Init failed - missing container");
            return;
        }

        let containerEl;

        if (typeof container === "string") {
            const containerEl = document.querySelector(container);
            if (!containerEl) {
                console.error("ThreeGlobeContoller Init failed - container element not found");
                return;
            }
        } else {
            containerEl = container;
        }

        this.options = { ...defaultOptions, ...options };
        this.ready = false;
        this.containerEl = containerEl;
        this.countries = null;
        this.globe = null;
        this.autoAnimateIntervalId = null;
        this.eventHandlers = {};
        this.flying = true;
        this.idleTimer = null;

        this._showLoadingIndicator();

        this.init();
        this._resizeHandler();
    }

    get flying() {
        return this._flying;
    }

    set flying(val) {
        this._flying = val;
        this._onPropertyChanged("flying", val);
    }

    _onPropertyChanged(propName, val) {
        // flying
        if (propName === "flying") {
            this._onFlyingChanged(val);
        }
    }

    _onFlyingChanged(val) {
        if (val === false) {
            this._idleTimerStart();
        } else {
            this._idleTimerStop();
        }
    }

    _idleTimerStart() {
        const { idleDuration } = this.options;
        if (this.idleTimer != null) this._idleTimerStop();

        this.idleTimer = setTimeout(() => {
            this._emitEvent("idle");
            this.idleTimer = null;
        }, idleDuration);
    }

    _idleTimerStop() {
        clearTimeout(this.idleTimer);
    }

    _onInteraction() {
        this._idleTimerStart();
    }

    _controls() {
        const { controls } = this.options;

        this.globe.controls().enableZoom = controls.enableZoom;
        this.globe.controls().enablePan = controls.enablePan;
        this.globe.controls().enableRotate = controls.enableRotate;
        const that = this;

        this.globe.controls().addEventListener("change", function() {
            if (!that.flying) {
                that._emitEvent("interaction");
                that._onInteraction();
            }
        });

        this.globe.onGlobeClick(function() {
            // that._emitEvent("interaction");
            // that._onInteraction();
        });

        this.globe.onGlobeReady(function() {
            that._emitEvent("globeReady");
            that.ready = true;
            that.flying = false;
        });
    }

    _showLoadingIndicator() {
        const loadingWrapEl = createEl("div", null, {
            className: "wpglobe-loader-wrap",
        });
        const loadingEl = createEl("div", null, { className: "wpglobe-loader" });
        loadingWrapEl.append(loadingEl);
        this.containerEl.append(loadingWrapEl);
    }

    async init() {
        // get countries geojson
        const res = await fetch(this.options.countriesGeoJsonUrl);
        this.countries = await res.json();

        let configOptions = {};
        if (this.options.autoAnimate) {
            configOptions.animateIn = false;
        }

        this.globe = Globe(configOptions)(this.containerEl);
        this.globeMaterial = this.globe.globeMaterial();

        this._controls();

        // set background transparent
        this.globe.backgroundColor("rgba(0, 0, 0, 0)");

        // set renderer size to container
        const containerW = this.containerEl.offsetWidth;
        const containerH = this.containerEl.offsetHeight;
        this.globe.width([containerW]);
        this.globe.height([containerH]);
        this.globe.renderer().setSize(containerW, containerH);

        this.globe.pointOfView(
            {
                lat: 0,
                lng: 0,
                altitude: this.options.flyToAltitude,
            },
            1000
        );

        this.globe.showAtmosphere(false);

        // build surface and pins
        this._globeSurface();

        // auto animate
        if (this.options.autoAnimate) {
            setTimeout(() => {
                this.pinsAutoAnimate();
            }, 100);
        }
    }

    _resizeHandler() {
        const that = this;
        window.addEventListener("resize", () => {
            const containerW = that.containerEl.offsetWidth;
            const containerH = that.containerEl.offsetHeight;
            const aspect = that.containerEl.offsetWidth / that.containerEl.offsetHeight;

            this.globe.width([containerW]);
            this.globe.height([containerH]);

            that.globe.camera().aspect = aspect;
            that.globe.camera().updateProjectionMatrix();
            that.globe.renderer().setSize(containerW, containerH);
        });
    }

    on(eventName, handler) {
        this.eventHandlers[eventName] = handler;
    }

    _emitEvent(eventName, payload = {}) {
        if (this.eventHandlers[eventName] === undefined) return;
        this.eventHandlers[eventName](payload);
    }

    _createPopupHTML(pin) {
        const el = createEl("div", null, { className: "map-pin-popup" });
        const closeBtn = createEl("button", "&times;", {
            className: "map-pin-popup__close",
        });

        const title = createEl("div", pin.title, {
            className: "map-pin-popup__title",
        });
        const description = createEl("div", pin.description, {
            className: "map-pin-popup__description",
        });

        const that = this;
        closeBtn.onclick = e => {
            e.preventDefault();
            that._togglePin(pin);
        };

        if (this.options.pinPopupCloseBtnEnabled) {
            el.append(closeBtn, title, description);
        } else {
            el.append(title, description);
        }

        return el;
    }

    _getPinElements(pinEl) {
        const svgIcon = pinEl.querySelector("svg");
        const popupEl = pinEl.querySelector(".card--globe");
        return {
            svgIcon,
            popupEl,
        };
    }

    _pinSvgColor(pinEl, state) {
        const { pinColor, pinActiveColor } = this.options;
        const { svgIcon } = this._getPinElements(pinEl);
        let color;
        switch (state) {
            case "active":
                color = pinActiveColor;
                break;
            case "default":
                color = pinColor;
                break;
        }
        svgIcon.style.fill = color;
    }

    _pinState(pin, state) {
        if (state === "active") {
            this._pinSvgColor(pin.el, "active");
            pin.isPinActive = true;
        } else if (state == "default") {
            this._pinSvgColor(pin.el, "default");
            pin.isPinActive = false;
        }
    }

    _popupToggle(pin, action) {
        const { popupEl } = this._getPinElements(pin.el);

        if (action == "open") {
            // OPEN
            pin.isPopupOpen = true;
            popupEl.style.visibility = "visible";

            setTimeout(() => {
                pin.el.classList.add("popup-showing");
            }, 0);
        } else if (action == "close") {
            // CLOSE
            pin.isPopupOpen = false;
            setTimeout(() => {
                pin.el.classList.remove("popup-showing");
                popupEl.style.visibility = "hidden";
            }, 0);
        }
    }

    _togglePin(pinId, flyTo = true, _popupAction = null, _pinState = null, deactivateActivePins = true) {
        // get pin
        const pin = this._getPinById(pinId);
        if (!pin) return;

        // determine popup action
        let popupAction = _popupAction;
        if (popupAction === null) {
            popupAction = pin.isPopupOpen ? "close" : "open";
        }

        // change pin state
        let pinState = _pinState;
        if (pinState == null) {
            pinState = pin.isPinActive ? "default" : "active";
        }
        // deactivate active pins
        deactivateActivePins && this._deactivateActivePins();

        this._pinState(pin, pinState);

        // toggle popup
        this._popupToggle(pin, popupAction);

        // fly to
        if (flyTo && pinState != "default") {
            this.flying = true;
            // fly!
            setTimeout(() => {
                this.globe.pointOfView(
                    {
                        lat: pin.lat,
                        lng: pin.lng,
                        altitude: this.options.flyToAltitude,
                    },
                    window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 1000
                );
            }, 10);

            setTimeout(() => {
                this.flying = false;
            }, 1300);
        }
    }

    _activePins() {
        return this.options.pins.filter(pin => pin.isPinActive === true);
    }

    _deactivateActivePins() {
        const activePins = this._activePins();
        if (!activePins) return;

        activePins.forEach(pin => {
            this._togglePin(pin.id, false, "close", "default", false);
        });
    }

    _flyToAltitude() {
        return window.innerWidth <= 768 ? this.options.flyToAltitudeMobile : this.options.flyToAltitude;
    }

    flyToPin(pinId, _options) {
        const defaultOptions = {
            openPopup: true,
        };
        const options = { ...defaultOptions, ..._options };

        const action = options.openPopup ? "open" : "close";

        this._togglePin(pinId, true, action);
    }

    _getPinById(pinId) {
        return this.options.pins.find(pin => pin.id === pinId);
    }

    _pinClickHandler(e, pinId) {
        this._emitEvent("pinClick", { pinId });

        const { target } = e;
        const pin = this._getPinById(pinId);
        const svgIcon = pin.el.querySelector("svg");
        const isWithinSvg = checkParent(svgIcon, target);
        const isSvg = target == svgIcon;
        if (!isWithinSvg && !isSvg) return;

        this._togglePin(pinId);
    }

    _postProcessing() {}

    _createPinHTML(pin) {
        const { pinColor, pinPulse } = this.options;
        let pinIcon = this.options.pinIcons.find(pin => pin.id == this.options.pinIconSelected);

        let pinHTML = pinIcon.icon;
        pinHTML = pinHTML.replace("$FILLCOLOR", pinColor);

        const el = document.createElement("div");
        el.className = "map-pin is-size-" + this.options.pinIconSizeSelected;
        if (!pinPulse) el.className += " pulse-disabled";
        el.innerHTML = pinHTML;
        el.id = `pin-${pin.id}`;

        el.style["pointer-events"] = "auto";
        el.style.cursor = "pointer";

        el.onclick = e => {
            this._pinClickHandler(e, pin.id);
        };

        let popupEl;
        if (typeof this.options.createPopupHTML === "function") {
            popupEl = this.options.createPopupHTML(pin, el, this.options);
        } else {
            popupEl = this._createPopupHTML(pin, el);
        }

        el.append(popupEl);
        return el;
    }

    async _globeSurface() {
        const {
            surfaceType,
            globeMaterialPolygons,
            activeCountries,
            pinData,
            pointOfViewCoordinates,
            atmosphereColor,
            atmosphereAltitude,
        } = this.options;
        const mapImageUrl = maps[this.options.map];

        // countries
        let polyCountries = this.countries.features;
        if (activeCountries.length > 0) {
            polyCountries = this.countries.features.filter(d => activeCountries.includes(d.properties.ISO_A2));
        }

        // polygons
        if ("polygons" == surfaceType) {
            this.globe
                .polygonsData(polyCountries)
                .polygonCapColor(() => this.options.hexPolygonColor)
                .polygonSideColor(() => "rgba(0, 100, 0, 0.05)")
                .hexPolygonsData(null);

            // Atmosphere
            this.globe.atmosphereColor(atmosphereColor);
            this.globe.atmosphereAltitude(atmosphereAltitude);

            // Point of view
            this.globe.pointOfView(pointOfViewCoordinates);

            // Globe material
            this.globeMaterial.map = null;
            this.globeMaterial.normalMap = null;
            this.globeMaterial.needsUpdate = true;
            this.globeMaterial.shininess = globeMaterialPolygons.shininess;
            this.globeMaterial.opacity = globeMaterialPolygons.opacity;
            this.globeMaterial.color = new Color(globeMaterialPolygons.color);
        } else if ("hex" == surfaceType) {
            const hexPolygonColorHandler = country => {
                if (this.options.highlightedCountries.length === 0) {
                    return this.options.hexPolygonColor;
                } else {
                    const countryCode = country.properties.ISO_A2;
                    if (this.options.highlightedCountries.includes(countryCode)) {
                        return this.options.countryHighlightColor;
                    }
                    return this.options.hexPolygonColor;
                }
            };

            // hexagon
            this.globe
                .hexPolygonsData(polyCountries)
                .hexPolygonResolution(this.options.hexPolhexPolygonColorygonResolution)
                .hexPolygonMargin(this.options.hexPolygonMargin)
                .hexPolygonAltitude(this.options.hexPolygonAltitude)
                .hexPolygonColor(hexPolygonColorHandler);

            // Globe material
            this.globeMaterial.map = null;
            this.globeMaterial.normalMap = null;
            this.globeMaterial.needsUpdate = true;
            this.globeMaterial.shininess = globeMaterialPolygons.shininess;
            this.globeMaterial.opacity = globeMaterialPolygons.opacity;
            this.globeMaterial.color = new Color(globeMaterialPolygons.color);
        } else if ("image" == surfaceType) {
            // image
            this.globe.hexPolygonsData(null);

            new TextureLoader().load(mapImageUrl, texture => {
                this.globeMaterial.map = texture;
                this.globeMaterial.shininess = 15;
                this.globeMaterial.color = new Color("white");
                this.globeMaterial.needsUpdate = true;
                this.globeMaterial.opacity = globeMaterialPolygons.opacity;
            });
        }

        this.options.pins = [];
        // HTML pins
        if (pinData.length > 0) {
            this.globe.htmlElementsData(pinData).htmlElement(pin => {
                const el = this._createPinHTML(pin);
                this.options.pins.push({
                    ...pin,
                    el,
                    isPopupOpen: false,
                    isPinActive: false,
                });
                return el;
            });
        }

        // wait for scene to be populated (asynchronously)
        setTimeout(() => {
            this._lighting();
        }, 10);

        this._postProcessing();
    }

    _lighting() {
        const directionalLight = this._getChildObject("DirectionalLight");
        const ambientLight = this._getChildObject("AmbientLight");

        // directional light
        if (directionalLight) {
            // directionalLight.intensity = 0.2;
            // directionalLight.position.set(50, 100, 100);
            directionalLight.intensity = 0.1;
        }
        // ambient light
        if (ambientLight) {
            ambientLight.intensity = 1.4;
            // ambientLight.position.set(50, 100, 100);
        }
    }

    _getChildObject(type) {
        return this.globe.scene().children.find(obj3d => obj3d.type === type);
    }

    setAmbientLightColor(color) {
        this.options.ambientLightColor = color;
    }

    setAmbientLightIntensity(intensity) {
        this.options.ambientLightIntensity = intensity;
    }

    setSurfaceType(surfaceType) {
        this.options.surfaceType = surfaceType;
        this.init();
    }

    setAtmosphereColor(color) {
        this.options.atmosphereColor = color;
        this.globe.atmosphereColor(color);
    }

    setAtmosphereAltitude(altitude) {
        this.options.atmosphereAltitude = altitude;
        this.globe.atmosphereAltitude(altitude);
    }

    setGlobeSurfaceColor(color) {
        this.options.globeMaterialPolygons.color = color;
        this._globeSurface();
    }

    setGlobeSurfaceOpacity(opacity) {
        this.options.globeMaterialPolygons.opacity = opacity;
        this._globeSurface();
    }

    setGlobePolygonColor(color) {
        this.options.hexPolygonColor = color;
        this._globeSurface();
    }

    setGlobePolygonResolution(resolution) {
        this.options.hexPolygonResolution = resolution;
        this._globeSurface();
    }

    setGlobePolygonMargin(margin) {
        this.options.hexPolygonMargin = margin;
        this._globeSurface();
    }

    setGlobeMap(map) {
        this.options.map = map;
        if (this.options.surfaceType != "image") this.options.surfaceType = "image";
        this._globeSurface();
    }

    setActiveCountries(activeCountries) {
        this.options.activeCountries = activeCountries;
        this._globeSurface();
    }

    setPinData(pinData) {
        this.options.pinData = pinData;
        this._globeSurface();
    }

    setPinIcon(pinIconId) {
        this.options.pinIconSelected = pinIconId;
        this._globeSurface();
    }

    setPinIconSizeSelected(size) {
        this.options.pinIconSizeSelected = size;
        this._globeSurface();
    }

    setPinColor(color) {
        this.options.pinColor = color;
        this._globeSurface();
    }

    setPinActiveColor(color) {
        this.options.pinActiveColor = color;
        this._globeSurface();
    }

    setPinPulse(pinPulse) {
        this.options.pinPulse = pinPulse;
        this._globeSurface();
    }

    pinsAutoAnimate() {
        if (!this.options.pinData.length) return;

        this._emitEvent("autoAnimateStart");

        let index = 0;
        const pins = this.options.pins;
        const that = this;
        const duration = Number(this.options.autoAnimateDuration);
        this.autoAnimateIntervalId = setInterval(animateFnc, duration);

        function animateFnc() {
            let pin = pins[index];
            if (pin !== undefined) {
                that._togglePin(pin.id, true, "open");

                index++;
                if (index > pins.length - 1) {
                    index = 0;
                }
            } else {
                that.pinsStopAutoAnimate();
            }
        }

        animateFnc();
    }

    pinsStopAutoAnimate() {
        if (!this.autoAnimateIntervalId) return;

        this._emitEvent("autoAnimateStop");
        clearInterval(this.autoAnimateIntervalId);
        this.autoAnimateIntervalId = null;
    }

    setAutoAnimateDuration(duration) {
        this.options.autoAnimateDuration = duration;
        this.pinsStopAutoAnimate();
        this.pinsAutoAnimate();
    }
}
